import _axios from 'axios';
import { bg } from 'date-fns/locale';
import EventSource from 'eventsource';

import {
  getToken,
  setToken,
  getRefreshToken,
  setRefreshToken,
} from '../util/tokenStorage';

import {
  LOCAL_BASE_URL,
  PRODUCTION_BASE_URL,
  STAGING_BASE_URL,
} from '../../constants';
import toast from '../util/toasts';
import store from '../../store';
import { logout } from '../views/Login/loginSlice';

const axios = _axios.create({});

let HOST = LOCAL_BASE_URL;

switch (process.env.NODE_ENV) {
  case 'development':
    // HOST = STAGING_BASE_URL;
    HOST = LOCAL_BASE_URL;
    break;
  case 'staging':
    HOST = STAGING_BASE_URL;
    break;
  case 'production':
    HOST = PRODUCTION_BASE_URL;
    break;
}

// Checks for forbidden errors and displays a toast. Callers that treat a 403 as a
// legitimate "nothing to show you" opt out with skipForbiddenToast on the request config.
axios.interceptors.response.use(null, (error) => {
  const { status } = error.response || {};
  if (status === 403 && !error.config?.skipForbiddenToast) {
    toast.error('You cannot perform this action. Please contact your admin.');
  }
  return Promise.reject(error);
});

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
}

// Handle 401 errors globally: attempt silent token refresh, then retry; log out only if refresh fails
axios.interceptors.response.use(null, async (error) => {
  const { status } = error.response || {};
  const url = error.config?.url || '';
  const isAuthEndpoint =
    url.includes('/oauth2/token') || url.includes('/oauth2/refresh');

  if (status !== 401 || isAuthEndpoint) {
    return Promise.reject(error);
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    store.dispatch(logout());
    return Promise.reject(error);
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then((token) => {
      error.config.headers['token'] = token;
      return axios(error.config);
    });
  }

  isRefreshing = true;

  try {
    const response = await axios.post(HOST + '/oauth2/refresh', {
      refresh_token: refreshToken,
    });
    const newToken = response.data.token;
    setToken(newToken);
    if (response.data.refresh_token) {
      setRefreshToken(response.data.refresh_token);
    }
    processQueue(null, newToken);
    error.config.headers['token'] = newToken;
    return axios(error.config);
  } catch (refreshError) {
    processQueue(refreshError);
    store.dispatch(logout());
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
});

export async function api() {}

// SETUP
api.ready = async function () {
  return axios
    .get(HOST + '/ready', {})
    .then((res) => {
      return true;
    })
    .catch((err) => {
      // The API hides /ready from unauthenticated callers when an admin turns on the
      // hide-public-endpoints flag. A 404 is that gate, not an uninitialized node — treat it
      // as ready, or every visitor gets bounced into the setup wizard and can never log in.
      if (err.response && err.response.status === 404) {
        return true;
      }
      // Only an explicit "not initialized" answer sends the user to the setup wizard.
      if (
        err.response &&
        err.response.status === 503 &&
        err.response.data &&
        err.response.data.initialized === false
      ) {
        return false;
      }
      // Network failures, 5xx and any other unexpected response are errors, not "uninitialized".
      throw err;
    });
};

api.setupLogin = async function (username, password) {
  return axios
    .post(HOST + '/setup/login2/', {
      username: username,
      password: password,
    })
    .then((res) => {
      return res.data;
    });
};

api.registerAccount = async function (
  name,
  uuid,
  id_token,
  access_token,
  sub,
  email,
  username,
) {
  return axios
    .post(
      HOST + '/setup/register_account2',
      {
        node_name: name,
        node_uri: window.location.origin,
        org_uuid: uuid,
        cognito_id_token: id_token,
        cognito_access_token: access_token,
        sub: sub,
        cognito_email: email,
        cognito_username: username,
      },
      {},
    )
    .then((res) => {
      return res.data;
    });
};

// LOGIN CONFIG
api.loginConfigs = async function () {
  return axios.get(HOST + '/login-config').then((res) => res.data);
};

api.loginConfigs.post = async function (provider, client_id, redirect_url) {
  const token = getToken();
  return axios
    .post(
      HOST + '/login-config',
      { provider, client_id, redirect_url },
      { headers: { token } },
    )
    .then((res) => res.data);
};

api.loginConfigs.patch = async function (uuid, data) {
  const token = getToken();
  return axios
    .patch(`${HOST}/login-config/${uuid}`, data, { headers: { token } })
    .then((res) => res.data);
};

api.loginConfigs.delete = async function (uuid) {
  const token = getToken();
  return axios
    .delete(`${HOST}/login-config/${uuid}`, { headers: { token } })
    .then((res) => res.data);
};

// OAUTH EXCHANGE
api.oauth = {};
api.oauth.exchange = async function (code, provider) {
  return axios
    .post(HOST + '/oauth2/exchange', { code, provider })
    .then((res) => res.data);
};

// AUTH
api.auth = async function (username, password) {
  return axios
    .post(HOST + '/oauth2/token/', {
      username: username,
      password: password,
    })
    .then((res) => {
      return res.data;
    });
};

// PROJECTS
api.projects = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/projects', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

// USER
api.userPermissions = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/current-user', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

// TASKS
api.tasks = async function (project_uuid) {
  const token = getToken();
  return axios
    .get(HOST + '/projects/' + project_uuid + '/tasks', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data.results;
    });
};

api.tasks.getOne = async function (projectID, taskID) {
  const token = getToken();
  return axios
    .get(HOST + '/projects/' + projectID + '/tasks/' + taskID, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.tasks.create = async function (projectID, task) {
  const token = getToken();

  return axios
    .post(HOST + '/projects/' + projectID + '/tasks', task, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.tasks.delete = async function (projectID, taskID) {
  const token = getToken();

  return axios
    .delete(`${HOST}/projects/${projectID}/tasks/${taskID}`, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.tasks.put = async function (projectID, taskID, payload) {
  const token = getToken();

  return axios
    .put(HOST + '/projects/' + projectID + '/tasks/' + taskID, payload, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.tasks.execute = async function (projectID, taskID) {
  const token = getToken();
  return axios
    .post(
      HOST + '/projects/' + projectID + '/tasks/' + taskID + '/execute',
      null,
      {
        headers: {
          token: token,
        },
      },
    )
    .then((res) => res.data);
};

api.tasks.history = async function (projectID, taskID) {
  const token = getToken();
  return axios
    .get(`${HOST}/projects/${projectID}/tasks/${taskID}/versions`, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.tasks.dependencies = async function (projectID, taskID) {
  const token = getToken();
  return axios
    .get(`${HOST}/projects/${projectID}/tasks/${taskID}/dependencies`, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data.results);
};

// EXECUTIONS
api.executions = async function (projectID, params) {
  const token = getToken();
  const defaultParams = { created_start: 0 };
  params = Object.assign({}, defaultParams, params);
  return axios
    .get(HOST + '/projects/' + projectID + '/task_executions2', {
      params,
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data.results;
    });
};

api.executions.getOne = async function (projectID, executionID) {
  const token = getToken();
  const url = `${HOST}/projects/${projectID}/task_execution/${executionID}`;
  return axios
    .get(url, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.executions.kill = async function (projectID, executionID) {
  const token = getToken();
  const url = `${HOST}/projects/${projectID}/task_execution/${executionID}/kill`;
  return axios
    .put(url, null, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.executions.statsPlot = async function (projectID, executionID) {
  const token = getToken();
  const url = `${HOST}/projects/${projectID}/task_execution/${executionID}/stats-plot`;
  return axios
    .get(url, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.executions.logSource = function (projectID, executionID) {
  const token = getToken();
  return new EventSource(
    `${HOST}/projects/${projectID}/logs/execution/${executionID}/stream`,
    {
      headers: { token: token },
    },
  );
};

// SEQUENCES
api.sequences = async function (projectUuid) {
  const token = getToken();
  return axios
    .get(HOST + '/projects/' + projectUuid + '/sequences', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data.results;
    });
};

api.sequences.getAll = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/sequences', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data.results;
    });
};

api.sequences.getOne = async function (projectUUID, sequenceUUID) {
  const token = getToken();
  return axios
    .get(HOST + '/projects/' + projectUUID + '/sequences/' + sequenceUUID, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.sequences.delete = async function (projectUuid, sequenceID) {
  const token = getToken();
  return axios
    .delete(`${HOST}/projects/${projectUuid}/sequences/${sequenceID}`, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.sequences.postCron = async function (
  alias,
  enabled,
  schedule,
  project_uuid,
  sequence_tasks,
  description,
) {
  const token = getToken();

  let json = {
    source_sequence: null,
    trigger_type: 'cron',
    trigger_condition: 'always',

    project_uuid,
    enabled,
    alias,
    description,
    schedule,
    sequence_tasks,
  };

  return axios
    .post(HOST + '/projects/' + project_uuid + '/sequences', json, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data.results;
    });
};

api.sequences.postCronGraph = async function (
  alias,
  enabled,
  schedule,
  project_uuid,
  sequence_task_graph,
  description,
) {
  const token = getToken();

  let json = {
    source_sequence: null,
    trigger_type: 'cron',
    trigger_condition: 'always',

    project_uuid,
    enabled,
    alias,
    description,
    schedule,
    sequence_task_graph,
  };

  return axios
    .post(HOST + '/projects/' + project_uuid + '/sequences2', json, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data.results;
    });
};

api.sequences.postSequence = async function (
  alias,
  enabled,
  source_sequence,
  project_uuid,
  sequence_tasks,
  trigger_condition,
  description,
) {
  const token = getToken();

  let json = {
    schedule: '',
    trigger_type: 'sequence',

    project_uuid,
    enabled,
    alias,
    description,
    source_sequence,
    sequence_tasks,
    trigger_condition: trigger_condition || 'always',
  };

  return axios
    .post(HOST + '/projects/' + project_uuid + '/sequences', json, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data.results;
    });
};

api.sequences.postSequenceGraph = async function (
  alias,
  enabled,
  source_sequence,
  project_uuid,
  sequence_task_graph,
  trigger_condition,
  description,
) {
  const token = getToken();

  let json = {
    schedule: '',
    trigger_type: 'sequence',

    project_uuid,
    enabled,
    alias,
    description,
    source_sequence,
    sequence_task_graph,
    trigger_condition: trigger_condition || 'always',
  };

  return axios
    .post(HOST + '/projects/' + project_uuid + '/sequences2', json, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data.results;
    });
};

api.sequences.putCron = async function (
  sequenceUUID,
  alias,
  enabled,
  schedule,
  project_uuid,
  sequence_tasks,
  description,
) {
  const token = getToken();

  let json = {
    source_sequence: null,
    trigger_type: 'cron',
    trigger_condition: 'always',

    project_uuid,
    enabled,
    alias,
    description,
    schedule,
    sequence_tasks,
  };

  return axios
    .put(
      HOST + '/projects/' + project_uuid + '/sequences/' + sequenceUUID,
      json,
      {
        headers: {
          token: token,
        },
      },
    )
    .then((res) => {
      return res.data.results;
    });
};

api.sequences.putCronGraph = async function (
  sequenceUUID,
  alias,
  enabled,
  schedule,
  project_uuid,
  sequence_task_graph,
  description,
) {
  const token = getToken();

  let json = {
    source_sequence: null,
    trigger_type: 'cron',
    trigger_condition: 'always',

    project_uuid,
    enabled,
    alias,
    description,
    schedule,
    sequence_task_graph,
  };

  return axios
    .put(
      HOST + '/projects/' + project_uuid + '/sequences2/' + sequenceUUID,
      json,
      {
        headers: {
          token: token,
        },
      },
    )
    .then((res) => {
      return res.data.results;
    });
};

api.sequences.putSequence = async function (
  sequenceUUID,
  alias,
  enabled,
  source_sequence,
  project_uuid,
  sequence_tasks,
  trigger_condition,
  description,
) {
  const token = getToken();

  let json = {
    schedule: '',
    trigger_type: 'sequence',

    project_uuid,
    enabled,
    alias,
    description,
    source_sequence,
    sequence_tasks,
    trigger_condition: trigger_condition || 'always',
  };

  return axios
    .put(
      HOST + '/projects/' + project_uuid + '/sequences/' + sequenceUUID,
      json,
      {
        headers: {
          token: token,
        },
      },
    )
    .then((res) => {
      return res.data.results;
    });
};

api.sequences.putSequenceGraph = async function (
  sequenceUUID,
  alias,
  enabled,
  source_sequence,
  project_uuid,
  sequence_task_graph,
  trigger_condition,
  description,
) {
  const token = getToken();

  let json = {
    schedule: '',
    trigger_type: 'sequence',

    project_uuid,
    enabled,
    alias,
    description,
    source_sequence,
    sequence_task_graph,
    trigger_condition: trigger_condition || 'always',
  };

  return axios
    .put(
      HOST + '/projects/' + project_uuid + '/sequences2/' + sequenceUUID,
      json,
      {
        headers: {
          token: token,
        },
      },
    )
    .then((res) => {
      return res.data.results;
    });
};

api.sequences.run = async function (projectID, sequenceID) {
  const token = getToken();
  const url = `${HOST}/projects/${projectID}/sequences/${sequenceID}/run`;
  return axios
    .post(url, null, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.sequences.patch = async function (projectID, sequenceID, patch) {
  const token = getToken();
  const url = `${HOST}/projects/${projectID}/sequences/${sequenceID}`;
  return axios
    .patch(url, patch, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.sequences.executions = async function (projectID, sequenceID, filters) {
  const token = getToken();
  const url = `${HOST}/projects/${projectID}/${sequenceID}/sequence_executions`;
  return axios
    .get(url, {
      params: filters,
      headers: {
        token: token,
      },
    })
    .then((res) => res.data.results);
};

api.sequences.executions.tasks = async function (
  projectID,
  sequenceExecutionID,
  filters,
) {
  const token = getToken();
  const url = `${HOST}/projects/${projectID}/task_executions`;
  return axios
    .get(url, {
      params: {
        ...filters,
        sequence_execution_uuid: sequenceExecutionID,
      },
      headers: {
        token: token,
      },
    })
    .then((res) => res.data.results);
};

api.sequences.kill = async function (projectID, executionID, filters) {
  const token = getToken();
  const url = `${HOST}/projects/${projectID}/sequence_execution/${executionID}/kill`;
  return axios
    .get(url, {
      params: filters,
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

// ENGINES
api.engines = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/engines2', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data.results;
    });
};

// Global admin only. Hides/shows an engine in the create-task app picker.
api.setEngineDisabled = async function (uuid, disabled) {
  const token = getToken();
  return axios
    .patch(
      HOST + '/admin/engines/' + uuid,
      { disabled },
      {
        headers: {
          token: token,
        },
      },
    )
    .then((res) => res.data);
};

// CONNECTIONS
api.connections = async function (projectID) {
  const token = getToken();
  return axios
    .get(`${HOST}/projects/${projectID}/connections`, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.connections.history = async function (projectID, connectionID) {
  const token = getToken();
  return axios
    .get(`${HOST}/projects/${projectID}/connections/${connectionID}/versions`, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

// Pass { inUse: true } to restrict to connection types used by active apps.
api.connectionTypes = async function ({ inUse = false } = {}) {
  const token = getToken();

  return axios
    .get(HOST + '/connection-types', {
      headers: {
        token: token,
      },
      ...(inUse ? { params: { in_use: true } } : {}),
    })
    .then((res) => {
      return res.data.results;
    });
};

api.connectionDetail = async function (projectID, connectionID) {
  const token = getToken();
  const url = `${HOST}/projects/${projectID}/connections/${connectionID}`;
  return axios
    .get(url, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

// this UPDATES a connection
api.connectionDetail.put = async function (
  alias,
  connection_type_uuid,
  parameters,
  projectUuid,
  connectionUuid,
) {
  const token = getToken();
  const json = {
    alias: alias,
    connection_type_uuid: connection_type_uuid,
    project_uuid: projectUuid,
    parameters: parameters,
  };

  const url = `${HOST}/projects/${projectUuid}/connections/${connectionUuid}`;
  return axios.put(url, json, { headers: { token: token } }).then((res) => {
    return res.data;
  });
};

// this CREATES a connection
// TODO: FIX THIS
api.connectionDetail.post = async function (
  alias,
  connection_type_uuid,
  parameters,
  projectUuid,
) {
  const token = getToken();
  const json = {
    alias: alias,
    connection_type_uuid: connection_type_uuid,
    project_uuid: projectUuid,
    parameters: parameters,
  };

  return axios
    .post(HOST + '/projects/' + projectUuid + '/connections', json, {
      headers: { token: token },
    })
    .then((res) => {
      return res.data;
    });
};

api.connectionDetail.delete = async function (projectID, connectionID) {
  const token = getToken();
  const url = `${HOST}/projects/${projectID}/connections/${connectionID}`;
  return axios
    .delete(url, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      // Mirrors api.sharedConfigs.delete: `detail` is either a string or, for an
      // HTTP 409 in-use conflict, a structured `{ message, blockers }` object.
      // Fall back to the axios message so a network failure (no response body)
      // is never mistaken for a successful delete.
      const detail = error.response?.data?.detail;
      return {
        error: detail || error.message || 'Failed to delete connection.',
      };
    });
};

// SHARED CONFIGS

// Reads of shared configs/fields are gated by can_manage_shared_* on the user. The task
// and connection editors fetch these lists for everyone, so a 403 there means "you have
// none to pick from", not an error worth shouting about. Writes still toast.
function emptyOnForbidden(err) {
  if (err.response && err.response.status === 403) {
    return { results: [] };
  }
  throw err;
}

api.sharedConfigTypes = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/shared-config-types/', {
      headers: {
        token: token,
      },
      skipForbiddenToast: true,
    })
    .then((res) => {
      return res.data;
    })
    .catch(emptyOnForbidden);
};

// `sharedConfigTypeUuid` narrows the list to one shared-config type server-side — the
// type a `shared_config` parameter declares via `shared_config_type_uuid`. Omit it to get
// every config (the Shared Configs list view, and specs that declare no type).
api.sharedConfigs = async function (sharedConfigTypeUuid) {
  const token = getToken();
  return axios
    .get(HOST + '/shared-configs/', {
      headers: {
        token: token,
      },
      ...(sharedConfigTypeUuid && {
        params: { shared_config_type_uuid: sharedConfigTypeUuid },
      }),
      skipForbiddenToast: true,
    })
    .then((res) => {
      return res.data;
    })
    .catch(emptyOnForbidden);
};

api.sharedConfigs.post = async function (typeUUID, alias, parameters) {
  const token = getToken();
  const json = {
    alias: alias,
    shared_config_type_uuid: typeUUID,
    parameters: parameters,
  };

  return axios
    .post(HOST + '/shared-configs/', json, { headers: { token: token } })
    .then((res) => {
      return res.data;
    });
};

api.sharedConfigs.delete = async function (uuid) {
  const token = getToken();
  return axios
    .delete(HOST + '/shared-configs/' + uuid, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      // `detail` (and thus `error`) can be either a string (legacy message) or
      // a structured object `{ message, blockers }` for HTTP 409 conflicts —
      // raised when a task still references this row. Return it as-is so the
      // structured object passes through unchanged.
      const detail = error.response?.data?.detail;
      return { error: detail };
    });
};

api.sharedConfigs.put = async function (uuid, typeUUID, parameters) {
  const token = getToken();
  let x = {};
  const json = {
    alias: parameters.alias,
    parameters: parameters,
    shared_config_type_uuid: typeUUID,
  };

  return axios
    .put(HOST + '/shared-configs/' + uuid + '/', json, {
      headers: { token: token },
    })
    .then((res) => {
      return res.data;
    });
};

api.sharedConfigs.usage = async function (uuid) {
  const token = getToken();
  return axios
    .get(`${HOST}/shared-configs/${uuid}/usage`, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.sharedConfigs.history = async function (configID) {
  const token = getToken();
  return axios
    .get(`${HOST}/shared-configs/${configID}/versions`, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

// SHARED FIELDS
api.sharedFields = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/shared-fields/', {
      headers: {
        token: token,
      },
      skipForbiddenToast: true,
    })
    .then((res) => {
      return res.data;
    })
    .catch(emptyOnForbidden);
};

api.sharedFields.put = async function (sharedField, uuid) {
  const token = getToken();
  const json = {
    parameters: sharedField.parameters,
    field_type: sharedField.field_type,
    alias: sharedField.alias,
    options: sharedField.options,
    categories: sharedField.categories,
  };

  return axios
    .put(HOST + '/shared-fields/' + uuid + '/', json, {
      headers: { token: token },
    })
    .then((res) => {
      return res.data;
    });
};

api.sharedFields.post = async function (
  field_type,
  alias,
  parameters,
  categories,
  options,
) {
  const token = getToken();
  const json = {
    parameters: parameters,
    field_type: field_type,
    alias: alias,
    options: options,
    categories: categories,
  };

  return axios
    .post(HOST + '/shared-fields/', json, { headers: { token: token } })
    .then((res) => {
      return res.data;
    });
};

api.sharedFields.delete = async function (uuid) {
  const token = getToken();
  return axios
    .delete(`${HOST}/shared-fields/${uuid}`, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      // `detail` (and thus `error`) can be either a string (legacy message) or
      // a structured object `{ message, blockers }` for HTTP 409 conflicts —
      // raised when a task still references this row. Return it as-is so the
      // structured object passes through unchanged.
      const detail = error.response?.data?.detail;
      return { error: detail };
    });
};

api.sharedFields.usage = async function (uuid) {
  const token = getToken();
  return axios
    .get(`${HOST}/shared-fields/${uuid}/usage`, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.sharedFields.categories = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/parameter-categories', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

// DASHBOARD
api.dashboard = async function (projectId, options = {}) {
  const token = getToken();
  const params = {};
  if (options.period_days) params.period_days = options.period_days;
  if (options.from_date) params.from_date = options.from_date;
  if (options.to_date) params.to_date = options.to_date;

  return axios
    .get(`${HOST}/projects/${projectId}/dashboard`, {
      params,
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

// ADMIN
// SYSTEMLOCK
api.systemlock = {};

// STATUS PAGE
api.status = {};

api.status.versions = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/admin/upgrade/available-versions', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.status.latest = async function () {
  return axios
    .get('https://nomnomdata.builtwithdark.com/current_version', {})
    .then((res) => {
      return res.data;
    });
};

api.systemlock.executions = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/admin/systemlock/executions', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.systemlock.executions.set = async function () {
  const token = getToken();
  return axios
    .post(
      HOST + '/admin/systemlock/executions/',
      {},
      {
        headers: { token: token },
      },
    )
    .then((res) => {
      return res.data;
    });
};

api.systemlock.executions.delete = async function () {
  const token = getToken();
  return axios
    .delete(HOST + '/admin/systemlock/executions/', {
      headers: { token: token },
    })
    .then((res) => {
      return res.data;
    });
};

// TODO: FIX API TO MAKE THIS WORK
api.status.logSource = function () {
  const token = getToken();
  return new EventSource(`${HOST}/logs/system/stream`, {
    headers: { token: token },
  });
};

// ADMIN
api.admin = {};
api.admin.dashboard = async function (filters = {}) {
  const token = getToken();
  const params = {};
  if (filters.period_days) params.period_days = filters.period_days;
  if (filters.from_date) params.from_date = filters.from_date;
  if (filters.to_date) params.to_date = filters.to_date;
  return axios
    .get(HOST + '/admin/dashboard', {
      headers: { token: token },
      params,
    })
    .then((res) => res.data);
};

api.admin.resourceUsage = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/admin/resource-usage', { headers: { token } })
    .then((res) => res.data);
};

api.admin.dashboardConfig = {};
api.admin.dashboardConfig.get = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/admin/dashboard/config', {
      headers: { token: token },
    })
    .then((res) => res.data);
};

// Send only the fields being changed — the API applies a partial update, so omitted
// settings keep their current values.
api.admin.dashboardConfig.set = async function (body) {
  const token = getToken();
  return axios
    .put(HOST + '/admin/dashboard/config', body, {
      headers: { token: token },
    })
    .then((res) => res.data);
};

api.admin.upgrade = {};
api.admin.upgrade.available = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/admin/upgrade/available', {
      headers: { token: token },
    })
    .then((res) => res.data);
};
api.admin.upgrade.start = async function (body) {
  const token = getToken();
  return axios
    .post(HOST + '/admin/upgrade', body, {
      headers: { token: token },
    })
    .then((res) => res.data);
};
api.admin.upgrade.status = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/admin/upgrade/status', { headers: { token } })
    .then((res) => res.data);
};

// PERMISSIONS
api.users = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/users/', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.users.patch = async function (userUuid, payload) {
  const token = getToken();
  return axios
    .patch(`${HOST}/users/${userUuid}`, payload, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.users.delete = async function (userUuid) {
  const token = getToken();
  return axios
    .delete(`${HOST}/users/${userUuid}`, {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.groups = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/groups/', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.groups.post = async function (name) {
  const token = getToken();
  const json = {
    avatar: null,
    description: '',
    name: name,
    type: 'project',
  };

  return axios
    .post(HOST + '/groups/', json, { headers: { token: token } })
    .then((res) => {
      return res.data;
    });
};

api.groups.user = {};
api.groups.user.add = async function (group_uuid, user_uuid) {
  const token = getToken();
  const json = {};

  return axios
    .put(HOST + '/groups/' + group_uuid + '/users/' + user_uuid, json, {
      headers: { token: token },
    })
    .then((res) => {
      return res.data;
    });
};

api.groups.user.remove = async function (group_uuid, user_uuid) {
  const token = getToken();

  return axios
    .delete(HOST + '/groups/' + group_uuid + '/users/' + user_uuid, {
      headers: { token: token },
    })
    .then((res) => {
      return res.data;
    });
};

api.groups.role = {};
api.groups.role.add = async function (group_uuid, role_uuid) {
  const token = getToken();
  const json = {};

  return axios
    .put(HOST + '/groups/' + group_uuid + '/roles/' + role_uuid, json, {
      headers: { token: token },
    })
    .then((res) => {
      return res.data;
    });
};

api.groups.role.remove = async function (group_uuid, role_uuid) {
  const token = getToken();

  return axios
    .delete(HOST + '/groups/' + group_uuid + '/roles/' + role_uuid, {
      headers: { token: token },
    })
    .then((res) => {
      return res.data;
    });
};

api.groups.delete = async function (uuid) {
  const token = getToken();

  return axios
    .delete(`${HOST}/groups/${uuid}`, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.projects = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/projects/', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.projects.post = async function (alias, description) {
  const token = getToken();
  const json = {
    alias: alias,
    description: description,
  };

  return axios
    .post(HOST + '/projects/', json, { headers: { token: token } })
    .then((res) => {
      return res.data;
    });
};

api.projects.put = async function (uuid, alias, description) {
  const token = getToken();
  const json = {
    alias: alias,
    description: description,
  };

  return axios
    .put(HOST + '/projects/' + uuid + '/', json, { headers: { token: token } })
    .then((res) => {
      return res.data;
    });
};

api.projects.delete = async function (uuid) {
  const token = getToken();

  return axios
    .delete(`${HOST}/projects/${uuid}/`, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data)
    .catch((error) => {
      // `detail` (and thus `error`) can be either a string (legacy message) or
      // a structured object `{ message, blockers }` for HTTP 409 conflicts.
      // Return it as-is so the structured object passes through unchanged.
      const detail = error.response?.data?.detail;
      return { error: detail };
    });
};

api.projects.getOne = async function (uuid) {
  const token = getToken();
  return axios
    .get(`${HOST}/projects/${uuid}/`, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.projects.users = async function (uuid) {
  const token = getToken();
  return axios
    .get(`${HOST}/projects/${uuid}/users`, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.projects.users.add = async function (projectUuid, userUuid, role) {
  const token = getToken();
  return axios
    .put(
      `${HOST}/projects/${projectUuid}/users/${userUuid}`,
      { role },
      {
        headers: {
          token: token,
        },
      },
    )
    .then((res) => res.data);
};

api.projects.users.remove = async function (projectUuid, userUuid) {
  const token = getToken();
  return axios
    .delete(`${HOST}/projects/${projectUuid}/users/${userUuid}`, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.roles = async function () {
  const token = getToken();
  return axios
    .get(HOST + '/roles/', {
      headers: {
        token: token,
      },
    })
    .then((res) => {
      return res.data;
    });
};

api.roles.post = async function (name, project_uuid, actions, resources) {
  const token = getToken();
  const json = {
    role_name: name,
    role_type: 'project',
    actions: actions,
    resources: resources,
    description: '',
    project_uuid: project_uuid,
  };

  return axios
    .post(HOST + '/projects/' + project_uuid + '/roles', json, {
      headers: { token: token },
    })
    .then((res) => {
      return res.data;
    });
};

api.roles.put = async function (name, uuid, project_uuid, actions, resources) {
  const token = getToken();
  const json = {
    actions: actions,
    description: '',
    project_uuid: project_uuid,
    resources: resources,
    role_name: name,
    role_type: 'project',
  };

  return axios
    .put(HOST + '/projects/' + project_uuid + '/roles/' + uuid, json, {
      headers: { token: token },
    })
    .then((res) => {
      return res.data;
    });
};

api.roles.delete = async function (project_uuid, uuid) {
  const token = getToken();

  return axios
    .delete(`${HOST}/projects/${project_uuid}/roles/${uuid}`, {
      headers: {
        token: token,
      },
    })
    .then((res) => res.data);
};

api.version = async function () {
  const token = getToken();

  return axios
    .get(HOST + '/version', {
      headers: { token: token },
    })
    .then((res) => res.data);
};
